import type { AssetWorkflowOptions } from '../../types';

export const createLabelProperties: AssetWorkflowOptions = {
  workflowKey: 'addLabelPropertiesWorkflow',
  workflowType: 'addAssetWorkflow',
  assetType: 'labelProperties',
  workflowIndex: 1,
  workflowName: 'Add Label Properties',
  workflowDescription: `Tell us about the details of your labels so we can generate them on your behalf`,
  workflowStepOptions: [
    {
      workflowStepKey: 'linearUnit',
      workflowStepNavigationName: 'Linear Unit',
      workflowComponent: {
        workflowComponentType: 'singleSelect',
        prompt: 'What units do you use?',
        description: `Select the linear unit that you will use to express dimensions`,
        help: '',
        options: [
          { optionText: 'cm (Centimetres)', optionValue: 'cm' },
          { optionText: 'in (Inches)', optionValue: 'in' },
        ],
      },
      workflowStateToAssetMapping: {
        mappingType: 'property',
        property: 'linearUnits',
      },
    },
    {
      workflowStepKey: 'height',
      workflowStepNavigationName: 'Label Height',
      excludeFromNavigation: false,
      refreshAssetsOnStepCompletion: true,
      workflowComponent: {
        validationRegex: new RegExp('^[0-9]+$'),
        validationMessage: 'Height must be a number',
        workflowComponentType: 'textEntry',
        prompt: 'What is the height of your labels?',
        description: 'Enter a label height in units you previously selected',
        help: '',
      },
      workflowStateToAssetMapping: {
        mappingType: 'property',
        property: 'height',
      },
    },
    {
      workflowStepKey: 'width',
      workflowStepNavigationName: 'Label Width',
      excludeFromNavigation: false,
      refreshAssetsOnStepCompletion: true,
      workflowComponent: {
        validationRegex: new RegExp('^[0-9]+$'),
        validationMessage: 'Width must be a number',
        workflowComponentType: 'textEntry',
        prompt: 'What is the width of your labels?',
        description: 'Enter a label width in units you previously selected',
        help: '',
      },
      workflowStateToAssetMapping: {
        mappingType: 'property',
        property: 'width',
      },
    },
    {
      workflowStepKey: 'labelOrientation',
      workflowStepNavigationName: 'Label Orientation',
      workflowComponent: {
        workflowComponentType: 'singleSelect',
        prompt: 'Select label orientation?',
        description: `How will these labels be rotated?`,
        help: '',
        options: [
          { optionText: 'No Rotation', optionValue: 'rotate_0' },
          { optionText: '90 Degrees', optionValue: 'rotate_90' },
          { optionText: '180 Degrees', optionValue: 'rotate_180' },
          { optionText: '270 Degrees', optionValue: 'rotate_270' },
        ],
      },
      workflowStateToAssetMapping: {
        mappingType: 'property',
        property: 'labelOrientation',
      },
    },
    {
      workflowStepKey: 'dpi',
      workflowStepNavigationName: 'Label DPI',
      workflowComponent: {
        workflowComponentType: 'singleSelect',
        rows: 1,
        prompt: 'What DPI do you want to print labels with?',
        description: ``,
        help: 'DPI stands for Dots Per Inch which means how many dots of printing you get per of inch.  It is also known as Resolution. Select a DPI that works for your label printer',
        options: [
          { optionText: '203', optionValue: '203' },
          { optionText: '300', optionValue: '300' },
          { optionText: '600', optionValue: '600' },
        ],
      },
      workflowStateToAssetMapping: {
        mappingType: 'property',
        property: 'dpi',
      },
    },
    {
      workflowStepKey: 'nonZplProperties',
      workflowStepNavigationName: 'Non-ZPL Properties',
      workflowComponent: {
        workflowComponentType: 'nonZplEntry',
        prompt: 'Do you have different properties for non-ZPL labels?',
        description: '',
        help: 'This overrides Label Property values for non-ZPL labels.',
      },

      workflowStateToAssetMapping: {
        mappingType: 'property',
        property: 'nonZplLabelDimensions',
      },
    },
    {
      workflowStepKey: 'partnerProvidedId',
      workflowStepNavigationName: 'Label Properties ID Alias',
      optional: true,
      workflowComponent: {
        workflowComponentType: 'textEntry',
        prompt: 'Enter a unique identifier for these label properties',
        description: 'This is optional but will allow you to more easily reference these label properies in API calls.',
        help: '',
      },
      includeInDefaultAssetNameRules: {
        includeInDefaultAssetName: true,
      },
      workflowStateToAssetMapping: {
        mappingType: 'property',
        property: 'partnerProvidedId',
      },
    },
    {
      workflowStepKey: 'name',
      workflowStepNavigationName: 'Name',
      workflowComponent: {
        workflowComponentType: 'assetNameEntry',
        prompt: 'What will you call these label properties?',
        description: 'How will you refer to this set of label properties ',
        help: '',
      },
      workflowStateToAssetMapping: {
        mappingType: 'property',
        property: 'name',
      },
    },
    {
      workflowStepKey: 'description',
      workflowStepNavigationName: 'Description',
      workflowComponent: {
        workflowComponentType: 'textEntry',
        rows: 4,
        defaultValue: '',
        prompt: 'Enter a description (optional):',
        description: '',
        help: '',
      },
      workflowStateToAssetMapping: {
        mappingType: 'property',
        property: 'description',
      },
    },
    {
      workflowStepKey: 'tenantSelect',
      workflowStepNavigationName: 'Tenant Select',
      workflowComponent: {
        assetType: 'tenant',
        workflowComponentType: 'tenantSelect',
        prompt: 'Do these label properties belong to a specific tenant?',
        description: '',
        help: 'Specifying a tenant will allow you to more easily filter you various configuration pieces',
        optional: true,
      },
      workflowStepConditionalMatcher: [
        {
          assetType: 'tenant',
          schema: { type: 'array', minItems: 1 },
        },
      ],
      workflowStateToAssetMapping: {
        mappingType: 'link',
        linkAssetType: 'tenant',
        linkType: 'Tenant',
      },
    },
    {
      workflowStepKey: 'summary',
      workflowStepNavigationName: 'Configuration Summary',
      workflowComponent: {
        workflowComponentType: 'workflowSummary',
        title: 'Add Label Properties Summary',
        description: 'The Label Properties will be created.  Please review your selections.',
        help: '',
      },
      workflowStateToAssetMapping: {
        mappingType: 'ignore',
      },
    },
  ],
};
